'use client'

import { signIn } from 'next-auth/react'
import { useSearchParams, useRouter } from 'next/navigation'
import { Suspense, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Logomark } from '@/components/brand'
import { ProductShowcase } from '@/components/auth/ProductShowcase'
import { Lock, Zap, Globe } from 'lucide-react'

// Errors that indicate stale cookies that should be auto-cleared
const AUTO_CLEAR_ERRORS = [
  'OAuthSignin',
  'OAuthCallback',
  'OAuthCreateAccount',
  'Callback',
]

function clearAuthCookies() {
  const cookiesToClear = [
    'next-auth.session-token',
    '__Secure-next-auth.session-token',
    'next-auth.csrf-token',
    '__Secure-next-auth.csrf-token',
    'next-auth.callback-url',
    '__Secure-next-auth.callback-url',
    'next-auth.pkce.code_verifier',
    '__Secure-next-auth.pkce.code_verifier',
    'next-auth.state',
    '__Secure-next-auth.state',
  ]

  cookiesToClear.forEach((cookieName) => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`
  })
}

const HIGHLIGHTS = [
  {
    icon: <Lock className='h-4 w-4' strokeWidth={1.5} />,
    title: 'Enterprise SSO',
    desc: 'Secure single sign-on via your identity provider',
  },
  {
    icon: <Zap className='h-4 w-4' strokeWidth={1.5} />,
    title: 'AI-Powered',
    desc: 'Automated close, journals & reconciliations',
  },
  {
    icon: <Globe className='h-4 w-4' strokeWidth={1.5} />,
    title: 'Cloud-Native',
    desc: 'Deploy anywhere, scale everywhere',
  },
]

function SignInContent() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const callbackUrl = searchParams.get('callbackUrl') || '/'
  const error = searchParams.get('error')
  const [isClearing, setIsClearing] = useState(false)

  // Auto-clear cookies on OAuth errors and redirect to clean sign-in
  useEffect(() => {
    if (error && AUTO_CLEAR_ERRORS.includes(error)) {
      setIsClearing(true)
      clearAuthCookies()

      const timer = setTimeout(() => {
        const cleanUrl = new URL(window.location.href)
        cleanUrl.searchParams.delete('error')
        router.replace(cleanUrl.pathname + cleanUrl.search)
        setIsClearing(false)
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [error, router])

  if (isClearing) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-background'>
        <div className='h-8 w-8 animate-spin rounded-full border-4 border-brand-navy border-t-transparent' />
      </div>
    )
  }

  const getErrorMessage = (errorCode: string | null) => {
    switch (errorCode) {
      case 'AccessDenied':
        return 'You do not have permission to access this resource.'
      case 'Verification':
        return 'The verification link has expired or has already been used.'
      case 'SessionRequired':
        return 'Please sign in to access this page.'
      default:
        return errorCode ? 'An unexpected error occurred. Please try again.' : null
    }
  }

  const errorMessage = getErrorMessage(error)

  return (
    <div className='flex h-screen overflow-hidden'>
      {/* Left panel: Product Showcase (hidden on mobile) */}
      <div className='hidden w-[60%] lg:block'>
        <ProductShowcase />
      </div>

      {/* Right panel: Sign-in card with enhanced design */}
      <div className='relative flex h-screen w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/30 px-6 lg:w-[40%]'>
        {/* Ambient gradient blobs */}
        <div className='pointer-events-none absolute inset-0'>
          <div className='absolute -right-20 -top-20 h-[300px] w-[300px] rounded-full bg-brand-cornflower/[0.07] blur-[80px]' />
          <div className='absolute -bottom-20 -left-20 h-[250px] w-[250px] rounded-full bg-brand-purple/[0.05] blur-[60px]' />
        </div>

        {/* Content */}
        <div className='relative z-10 w-full max-w-md space-y-6'>
          {/* Tagline above card */}
          <motion.div
            className='text-center'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p className='text-sm font-medium tracking-wide text-brand-navy/60'>
              Intelligent close. Audit-ready.
            </p>
          </motion.div>

          {/* Sign-in card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Card className='relative overflow-hidden bg-white shadow-float-lg transition-shadow duration-500 hover:shadow-accent'>
              <CardWatermark opacity={4} scale={1} />
              <CardHeader className='relative z-10 space-y-3 pb-6 text-center'>
                <motion.div
                  className='mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-navy shadow-xl'
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    delay: 0.2,
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                  }}
                >
                  <Logomark variant='light' size={40} />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <CardTitle className='text-display-5 font-bold text-brand-navy'>
                    Supervity
                  </CardTitle>
                  <p className='mt-1 text-sm text-muted-foreground'>
                    Record to Report
                  </p>
                </motion.div>
              </CardHeader>
              <CardContent className='relative z-10 space-y-3 px-6 pb-6'>
                {/* Error Message */}
                {errorMessage && (
                  <motion.div
                    className='rounded-lg border border-red-200 bg-red-50 p-3 text-center text-sm text-red-700'
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <p>{errorMessage}</p>
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                >
                  <Button
                    onClick={() => signIn('keycloak', { callbackUrl })}
                    variant='gradient'
                    size='lg'
                    className='group w-full py-5 text-base'
                  >
                    Sign In with SSO
                    <Icons.arrowRight className='ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1' />
                  </Button>
                </motion.div>

                <motion.p
                  className='text-center text-xs text-muted-foreground'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                >
                  Use your organization credentials to sign in
                </motion.p>

                <motion.div
                  className='pt-2 text-center'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                >
                  <p className='text-sm text-muted-foreground'>
                    Don&apos;t have an account?{' '}
                    <a
                      href={`${process.env.NEXT_PUBLIC_BASE_PATH || ''}/auth/register`}
                      className='font-medium text-brand-cornflower transition-colors hover:text-brand-navy'
                    >
                      Register here
                    </a>
                  </p>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Feature highlights below card */}
          <motion.div
            className='grid grid-cols-3 gap-3'
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            {HIGHLIGHTS.map((h) => (
              <div
                key={h.title}
                className='rounded-xl bg-white/60 p-3 text-center backdrop-blur-sm'
              >
                <div className='mx-auto mb-1.5 flex h-8 w-8 items-center justify-center rounded-lg bg-brand-navy/5 text-brand-navy'>
                  {h.icon}
                </div>
                <p className='text-[11px] font-semibold text-brand-navy'>
                  {h.title}
                </p>
                <p className='mt-0.5 text-[10px] leading-tight text-muted-foreground'>
                  {h.desc}
                </p>
              </div>
            ))}
          </motion.div>

          {/* Mobile-only value prop */}
          <div className='text-center lg:hidden'>
            <p className='text-xs text-muted-foreground'>
              AI-first record-to-report (R2R) close platform
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function SignInPage() {
  return (
    <Suspense
      fallback={
        <div className='flex min-h-screen items-center justify-center bg-background'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-brand-navy border-t-transparent' />
        </div>
      }
    >
      <SignInContent />
    </Suspense>
  )
}
